import { TechnologyType } from '@@types/api.types';
import type { TechnologyCreateDto } from '@@types/api.types';
import {
  LONG_DESCRIPTION_MAX_LENGTH,
  LONG_DESCRIPTION_VALIDATION_MESSAGES,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  NAME_VALIDATION_MESSAGES,
} from '@azymmetric/shared/validation/validation.constants';
import { zCoercedOptional, zFilteredStringArray } from '@utils/zodv4.util';
import { z } from 'zod/v4';

/**
 * Base schema for technology metadata structure.
 * Validates research keywords with proper array filtering.
 */
const technologyMetadataSchema = z
  .object({
    researchKeywords: zFilteredStringArray(z.array(z.string())).optional(),
  })
  .strip();

/**
 * Base schema for technology timeline structure.
 * Validates timeline dates and optional validation status with description.
 * startDate is an ISO date string (YYYY-MM-DD).
 */
export const technologyTimelineSchema = z
  .object({
    description: zCoercedOptional(z.string().optional()).optional(),
    startDate: z.string().nullable().optional(),
    validated: z.boolean().optional(),
  })
  .strip();

/**
 * Main schema for validating technology creation/update data.
 * Used for both create and update operations with comprehensive validation.
 */
const technologyBaseSchema = z
  .object({
    categoryId: z.uuid({ message: 'Please select a category' }),
    description: zCoercedOptional(
      z.string().max(LONG_DESCRIPTION_MAX_LENGTH, { message: LONG_DESCRIPTION_VALIDATION_MESSAGES.max }).optional(),
    ),
    metadata: technologyMetadataSchema.optional(),
    name: z
      .string({ message: NAME_VALIDATION_MESSAGES.required })
      .trim()
      .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
      .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max }),
    primaryDomainId: z.uuid({ message: 'Please select a primary domain' }),
    primarySubdomainId: zCoercedOptional(z.uuid().optional()),
    primaryUseDescription: zCoercedOptional(z.string().optional()),
    primaryUseId: z.uuid({ message: 'Please select a primary use' }),
    subcategoryId: z.uuid({ message: 'Please select a subcategory' }),
    timeline: technologyTimelineSchema.optional(),
    type: z.enum([TechnologyType.HARDWARE, TechnologyType.SOFTWARE, TechnologyType.HYBRID], {
      message: 'Please select a technology type',
    }),
  })
  .strip();

export const technologySchema = technologyBaseSchema.transform((data): TechnologyCreateDto => {
  let timeline: TechnologyCreateDto['timeline'] = undefined;

  if (data.timeline?.startDate) {
    timeline = {
      description: data.timeline.description,
      startDate: data.timeline.startDate,
      validated: data.timeline.validated,
    };
  }

  return {
    categoryId: data.categoryId,
    description: data.description,
    metadata: data.metadata,
    name: data.name,
    primaryDomainId: data.primaryDomainId,
    primarySubdomainId: data.primarySubdomainId,
    primaryUseDescription: data.primaryUseDescription,
    primaryUseId: data.primaryUseId,
    subcategoryId: data.subcategoryId,
    timeline,
    type: data.type,
  };
}) satisfies z.ZodType<TechnologyCreateDto>;

/**
 * Schema for validating technology type step.
 * Ensures technology type selection is valid.
 */
export const typeStepSchema = technologyBaseSchema.pick({ type: true });

/**
 * Schema for validating technology information step.
 * Validates basic technology information including name, category, and description.
 */
export const informationStepSchema = technologyBaseSchema.pick({
  categoryId: true,
  description: true,
  name: true,
  subcategoryId: true,
});

/**
 * Schema for validating technology uses and domain step.
 * Validates primary use, domain, subdomain, and use description.
 */
export const usesAndDomainStepSchema = technologyBaseSchema.pick({
  primaryDomainId: true,
  primarySubdomainId: true,
  primaryUseDescription: true,
  primaryUseId: true,
});

/**
 * Schema for validating technology timeline and metadata step.
 * Validates timeline information and additional metadata.
 */
export const researchStepSchema = technologyBaseSchema.pick({
  metadata: true,
  timeline: true,
});

/**
 * Type for the form's internal state (uses startDate object for DatePicker).
 * This is the input type before transformation to TechnologyCreateDto.
 */
export type TechnologyFormData = z.input<typeof technologySchema>;
